import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Input } from 'reactstrap';
import { KeyboardKeys } from '@app/AppConstants';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { Loading } from '@app/Components/Loading';

type OnChangeHandler = (newText: string, oldText: string) => Promise<void> | Promise<boolean> | void;

type ReasonCellProps = {
    value: string;
    id: string;
    readonlyMode?: boolean;
    onChange: OnChangeHandler;
};

@observer
export class ReasonCell extends React.Component<ReasonCellProps, {}> {
    private _store: ReasonCellStore;

    constructor(props: ReasonCellProps) {
        super(props);
        this._store = new ReasonCellStore(props.value, props.onChange, props.readonlyMode);
    }
    
    componentDidUpdate(prevProps: Readonly<ReasonCellProps>): void {
        if(prevProps.id !== this.props.id){
            this._store.value = this.props.value;
        }
    }

    render() {
        if (this._store.mode === ViewMode.view) {
            return (
                <>
                    {<Loading loading={this._store.loader.isPending} />}
                    <div onClick={this._store.toEditMode} className="cell-with-textarea" title={this._store.value}>{this._store.value}</div>
                </>
            );
        } else {
            return (
                <div className="cell-with-textarea">
                    <Input
                        type="textarea"
                        value={this._store.value}
                        onChange={this._store.onEdit}
                        onBlur={this._store.onBlurHandler}
                        onKeyDown={this._store.onKeyDown}
                        onKeyUp={this._store.onKeyUp}
                        onFocus={this._store.onFocus}
                        autoFocus
                        autoComplete="off"
                    />
                </div>
            );
        }
    }

    static inputStyle() {
        return { width: '100%', minWidth: '150px', maxWidth: '250px', minHeight: '42px' };
    }
}

class ReasonCellStore {
    @observable value: string;
    @observable mode: string = ViewMode.view;
    private _initValue: string;
    private readonly _readonlyMode: boolean;
    private readonly _onChange: OnChangeHandler;
    public loader: PromiseCompletion = new PromiseCompletion();

    constructor(value: string, onChange: OnChangeHandler, _readonlyMode?: boolean) {
        this._initValue = value;
        this._onChange = onChange;
        this._readonlyMode = !!_readonlyMode;
        this.value = value || ''; //`value` prop on `input` should not be null.
    }

    @action.bound
    toEditMode(event: React.MouseEvent) {
        if (this._readonlyMode) return;
        event.preventDefault();
        event.stopPropagation();
        this.mode = ViewMode.edit;
    }

    @action.bound
    onEdit(event: React.ChangeEvent<HTMLInputElement>) {
        this.value = event.target.value;
    }

    @action.bound
    onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        const textArea = event.target as HTMLTextAreaElement;
        if (event.keyCode === KeyboardKeys.ENTER && !event.ctrlKey) {
            this.onBlurHandler();
            event.preventDefault();
        }

        if (event.keyCode === KeyboardKeys.ENTER && event.ctrlKey){
            document.execCommand('insertText', false, '\n');
            event.preventDefault();
        }

        if (event.keyCode === KeyboardKeys.ESCAPE) {
            this.value = this._initValue || '';
            this.mode = ViewMode.view;
            event.preventDefault();
        }
    }

    @action.bound
    onKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.resizeInput(target);
    }

    @action.bound
    onFocus(event: React.FocusEvent<HTMLInputElement>) {
        this.resizeInput(event.target);
    }

    @action.bound
    async onBlurHandler() {
        if (this._initValue !== this.value){
            const promise = this._onChange(this.value, this._initValue);
            if (promise){
                this.loader.subscribe(promise);
            }
            const result = await promise;
            if (result !== false){
                this._initValue = this.value;
            } else {
                this.value = this._initValue;
            }
        }
        this.mode = ViewMode.view;
    }

    @action.bound
    resizeInput(input: HTMLInputElement) {
        input.style.height = input.scrollHeight + 'px';
    }
}

class ViewMode {
    public static readonly view: string = 'view';
    public static readonly edit: string = 'edit';
}